<template>
	<div>
		<v-snackbar v-model="snackbarState">
			{{ snackbarMsg }}

			<template #actions>
				<v-btn color="white" variant="text" icon="mdi-close" density="compact" @click="snackbarState = false" />
			</template>
		</v-snackbar>

		<v-card :class="['scheduler-container', { 'is-expanded': isExpanded, 'is-embedded': embedded, 'is-selectable': selectable }]" elevation="0">
			<div v-if="!embedded" class="layout-top-nav-row">
				<SchedulerFilters v-model="activeFilter" :filter-options="displayedFilterOptions" @update:model-value="setUserActiveFilter" />
				<SchedulerNavigation
					:current-month="activeVisibleMonth"
					:can-go-previous-month="canGoPreviousMonth"
					:can-go-next-month="canGoNextMonth"
					:is-today-disabled="!isTodayInRange"
					@navigate="navigateMonth"
					@go-today="goToCurrentMonth"
					@toggle-panel="planningStore.setShowRightPanel(!planningStore.filters.showRightPanel)"
				/>
			</div>

			<div ref="calendarGridRef" class="calendar-master-scroll-viewport" @scroll.passive="handleMasterScroll">
				<div v-if="planningStore.initial" class="d-flex justify-center align-center h-100 py-10">
					<v-progress-circular indeterminate color="teal" :size="50" :width="7" />
				</div>
				<template v-else>
					<div class="calendar-header-sticky">
						<div class="scheduler-row layout-month-header-row">
							<div class="sidebar-column-sticky bg-white d-flex align-center">
								<div class="sidebar-fixed-title-part border-right d-flex align-center justify-space-between pl-3 h-100 mt-1">
									<v-icon v-if="!embedded" size="18" color="grey" class="cursor-pointer" @click="toggleShowUnallocated">
										{{ showUnallocated ? 'mdi-archive-off-outline' : 'mdi-archive-off' }}
									</v-icon>

									<v-switch
										v-if="previewStore.currentPreview != null"
										color="primary-green"
										:label="$t('planning_table.all_drivers')"
										v-model="showAllDrivers"
										hide-details
										class="switch-label-front"
									></v-switch>
								</div>
								<div
									v-if="isExpanded"
									class="sidebar-stats-part px-2 d-flex h-100"
									:class="selectedValues.length > 0 ? 'align-center justify-space-between' : 'align-start justify-center'"
									:style="dynamicStatsStyle"
								>
									<div v-if="selectedValues.length > 0" class="text-grey-darken-1 font-weight-medium" style="font-size: 14px; line-height: 1">
										{{ $t('navigation_statistics') }}
									</div>
									<StatsConfigMenu v-model="selectedValues" :options="options" @apply="apply" @reset="resetMenu" />
								</div>
							</div>

							<div class="calendar-column-flow" :style="virtualPaddingStyle">
								<div
									v-for="header in filteredMonthHeaders"
									:key="header.key"
									class="month-header-cell text-uppercase font-weight-bold"
									:style="header.style"
								>
									<span>{{ header.label }}</span>
								</div>
							</div>
						</div>

						<div class="scheduler-row layout-status-row">
							<div class="sidebar-column-sticky bg-white header-border-bottom h-100 d-flex align-center">
								<div class="sidebar-fixed-title-part border-right h-100"></div>

								<div v-if="isExpanded" class="sidebar-col-stats h-100 d-flex align-center" :style="dynamicStatsStyle">
									<div v-for="opt in SelectedStats" :key="opt.value" class="stat-header-cell h-100"></div>
									<div class="stat-header-cell extra-col border-right h-100"></div>
								</div>
							</div>

							<div class="calendar-column-flow" :style="virtualPaddingStyle">
								<div v-for="(day, index) in virtualDays" :key="'status-' + day.isoDate" class="planning-status-cell" :class="[day.statusClass]">
									<span v-if="isFirstStatusCell(index)" class="status-cell-label">
										{{ getStatusLabel(day.statusClass) }}
									</span>
								</div>
							</div>
						</div>

						<div class="scheduler-row layout-date-row border-bottom">
							<div class="sidebar-column-sticky bg-white d-flex align-center h-100">
								<div class="sidebar-title d-flex justify-space-between align-center border-right pr-2 pl-3 h-100">
									<div>{{ $t('title.persons') }}</div>
									<div class="pr-3">
										<v-icon size="16" color="grey-darken-1" class="cursor-pointer" @click="toggleSidebar">
											{{ isExpanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}
										</v-icon>
									</div>
								</div>

								<div v-if="isExpanded" class="sidebar-col-stats h-100 d-flex align-center" :style="dynamicStatsStyle">
									<div
										v-for="opt in SelectedStats"
										:key="opt.value"
										class="stat-header-cell d-flex align-center justify-center border-right h-100"
									>
										<v-icon size="15" color="grey">{{ opt.icon }}</v-icon>
									</div>

									<div class="stat-header-cell extra-col d-flex align-center justify-center border-right h-100"></div>
								</div>
							</div>

							<div class="calendar-column-flow" :style="virtualPaddingStyle">
								<div
									v-for="day in virtualDays"
									:key="`header-${day.isoDate}`"
									class="day-cell cursor-pointer"
									:class="day.dayClass"
									@contextmenu="showContextMenu($event, day)"
									@click="setSelectedDay(day)"
								>
									<small class="text-uppercase weekday-text mb-05">{{ day.weekdayLabel }}</small>
									<span class="day-number-text">{{ day.dayNum }}</span>
									<v-menu
										v-if="isContextMenuVisible(day)"
										v-model="showDayStatusActionMenu"
										:target="[contextMenuPosition.x, contextMenuPosition.y]"
										absolute
										offset-y
									>
										<div class="preview-dropdown">
											<v-list-item
												v-for="(item, index) in dayStatusActions"
												:key="index"
												class="px-2 text-body-2"
												@click.stop="onClickContextMenu(dayStatusActions[index])"
											>
												<v-list-item-title>{{ item.title }}</v-list-item-title>
											</v-list-item>
										</div>
									</v-menu>
								</div>
							</div>
						</div>

						<UnallocatedDutiesRow
							v-if="showUnallocated && !embedded"
							:total-count="totalUnallocatedCount"
							:is-row-expanded="isUnallocatedExpanded"
							:is-sidebar-expanded="isExpanded"
							:virtual-days="virtualDays"
							:virtual-padding-style="virtualPaddingStyle"
							:duties-by-day="unallocatedDutiesByDay"
							:selected-day-iso="selectedDayIso"
							@toggle-expand="toggleUnallocatedExpanded"
							@select-duty="(duty) => setSelectedDuty(duty, null)"
						/>
					</div>

					<div class="calendar-body" :style="verticalContainerStyle" @dblclick="handleGridDelegatedDblClick">
						<div
							v-for="(person, relativeIndex) in virtualDrivers"
							:key="person.id"
							class="scheduler-row content-row"
							:class="{ 'selected-driver-row': person.id === selectedDriver?.id }"
						>
							<DriverRowSidebar
								v-memo="[
									person.id,
									relativeIndex + verticalMetrics.start,
									isExpanded,
									stateLoading,
									applyValuesVersion,
									highlightedDriversSet.has(person.id),
									person.id === selectedDriver?.id,
									props.selectedDriverId === person.id,
									props.driverViolationsCounts ? props.driverViolationsCounts[person.id] : null,
								]"
								:person="person"
								:index="relativeIndex + 1 + verticalMetrics.start"
								:is-expanded="isExpanded"
								:stat-loading="stateLoading"
								:selected-stats="SelectedStats"
								:stat-options="options"
								:is-highlighted="highlightedDriversSet.has(person.id)"
								:row-class="{
									'highlighted-duty': highlightedDriversSet.has(person.id),
									'bg-white': !highlightedDriversSet.has(person.id),
									'selected-driver-row': person.id === selectedDriver?.id,
								}"
								:selectable="selectable"
								:selected="props.selectedDriverId === person.id"
								:violations-count="props.driverViolationsCounts ? (props.driverViolationsCounts[person.id] ?? null) : null"
								@select="selectDriver(person)"
								@toggle-select="emit('select-driver', person)"
								@open-stats-menu="
									selectDriver(person);
									activeStatsMenuDriver = person;
								"
							/>

							<div class="calendar-column-flow border-bottom" :style="virtualPaddingStyle">
								<div
									v-for="day in virtualDays"
									:key="person.id + day.isoDate"
									:ref="(el) => setCellRef(el, person.id, day.isoDate)"
									v-memo="[
										person.id,
										day.isoDate,
										day.id,
										getDutyForCell(person.id, day),
										isCellHighlighted(person.id, day.isoDate),
										selectedDayIso === day.isoDate,
										person.id === selectedDriver?.id,
										getActiveIndicators,
									]"
									class="grid-cell cursor-pointer"
									:style="getCellPreviewStyle(person.id, day)"
									@contextmenu.prevent="handleCellContextMenu($event, person, day)"
									:data-driver-id="person.id"
									:data-day-id="day.id"
									:data-iso-date="day.isoDate"
									:class="[
										day.weekendClass,
										{
											'month-boundary': day.isMonthBoundary,
											'highlighted-duty': isCellHighlighted(person.id, day.isoDate),
											'selected-day': selectedDayIso === day.isoDate || day.isoDate === embeddedTargetDateIso,
											'preview-change': isDutyInPreview(person.id, day),
											'preview-selected-change': isSelectedChangeLogDuty(person.id, day),
											'preview-violation': isDutyInViolation(person.id, day),
											'preview-selected-violation': isSelectedViolationDuty(person.id, day),
										},
									]"
									@click="highlightOnSingleClick(person, day)"
								>
									<DutyItem
										v-if="getDutyForCell(person.id, day)"
										:duty="getDutyForCell(person.id, day)"
										:active-indicators="getActiveIndicators"
										:embedded="embedded"
									/>
								</div>
							</div>

							<div v-if="driverViolationsCounts" class="violations-trailing-col d-flex align-center pl-3 border-bottom">
								<span class="text-caption text-grey-darken-1">
									{{ $t('solution.violations', { count: driverViolationsCounts[person.id] ?? 0 }) }}
								</span>
							</div>
						</div>
					</div>
				</template>
			</div>
			<PlanningDutyDialog
				v-if="selectedDuty"
				v-model="dialogState"
				:duty="selectedDuty"
				:isCustom="!!customDuties"
				@update:duty="handleUpdateDuty"
				@update:model-value="closeDialog()"
			/>

			<PlanningAssignDuty />
			<PlanningAbsenceContextMenu />
			<AbsenceInfoModal />
			<EditAbsenceModal />
		</v-card>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, shallowRef, onMounted, onBeforeMount, onUnmounted, nextTick } from 'vue';
import type { CSSProperties } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';

import SchedulerFilters from './TableComponents/SchedulerFilters.vue';
import SchedulerNavigation from './TableComponents/SchedulerNavigation.vue';
import StatsConfigMenu from './TableComponents/StatsConfigMenu.vue';
import UnallocatedDutiesRow from './TableComponents/UnallocatedDutiesRow.vue';
import DriverRowSidebar from './TableComponents/DriverRowSidebar.vue';
import DutyItem from '../Presentation/DutyItem.vue';
import PlanningDutyDialog from './Modals/PlanningDutyDialog.vue';
import PlanningAssignDuty from '@/rennovation/planning-view/components/Layout/Modals/PlanningAssignDuty.vue';
import PlanningAbsenceContextMenu from '@/rennovation/planning-view/components/Layout/Modals/PlanningAbsenceContextMenu.vue';
import AbsenceInfoModal from '@/rennovation/planning-view/components/Layout/Modals/AbsenceInfoModal.vue';
import EditAbsenceModal from '@/rennovation/planning-view/components/Layout/Modals/EditAbsenceModal.vue';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';

import { usePlanningAssignDutyStore } from '@/rennovation/planning-view/store/usePlanningAssignDutyStore';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { useDutyStore } from '@/rennovation/planning-view/store/useDutyStore';
import { usePlanningAbsenceMenuStore } from '@/rennovation/planning-view/store/usePlanningAbsenceMenuStore';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useDayStore, DayUiItem } from '@/rennovation/planning-view/store/useDayStore';
import { useUserStore } from '../../store';
import { STORAGE_KEYS, PLANNING_DAY_STATUS, SCROLL_STORAGE_KEY } from '../../constants';
import { useDriverStatsStyles } from '@/rennovation/planning-view/composables/useDriverStatsStyles';
import metricsService from '@/services/metricsService';

import type { Driver } from '../../types/models/user.ts';
import type { DriverStatKey, StatOption } from '../../types/components/types';
import type { DutyItemResponse } from '../../types/models/duty';
import type { ApiDriverUser } from '../../types/api/users-response';
import type { ApiDuty } from '@/rennovation/planning-view/types/api/duties-response';
import { usePreviewStore } from '../../store/usePreviewStore';
import { VIOLATION_CATEGORIES, getDutyCellStyle, type DayItem } from './TableComponents/PlanningTableUtils';
import { dayStatusPayload } from '@/rennovation/planning-view/store/useDayStore';

import { PlanningDayStatus } from '@/rennovation/planning-view/enums/planning-day-status';
import type { DayStatusAction, DayStatusClass, SelectedDay } from '../../types/api/days-response';

interface Position {
	x: number;
	y: number;
}

const props = withDefaults(
	defineProps<{
		drivers: Driver[];
		embedded?: boolean;
		targetDate?: string | null;
		selectable?: boolean;
		selectedDriverId?: string | null;
		driverViolationsCounts?: Record<string, number> | null;
		customDuties?: Record<string, Record<string, ApiDuty>> | null;
	}>(),
	{
		embedded: false,
		targetDate: null,
		selectable: false,
		selectedDriverId: null,
		driverViolationsCounts: null,
		customDuties: null,
	}
);

const emit = defineEmits(['select-driver']);
const { t } = useI18n();

const planningStore = usePlanningStore();
const dutyStore = useDutyStore();
const dayStore = useDayStore();
const userStore = useUserStore();
const assignDutyStore = usePlanningAssignDutyStore();
const dataStore = usePlanningBridgeStore();
const absenceMenuStore = usePlanningAbsenceMenuStore();
const { isActive } = useDutyChangePreview();

const ROW_HEIGHT = 40;
const VERTICAL_BUFFER = 6;
const CELL_WIDTH = 65;
const CELL_BUFFER = 6;
const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const STATUS_CLASS_MAP: Partial<Record<PlanningDayStatus, DayStatusClass>> = {
	[PlanningDayStatus.PAST]: 'status-past',
	[PlanningDayStatus.PUBLISHED]: 'status-published',
	[PlanningDayStatus.PREVIEW]: 'status-preview',
	[PlanningDayStatus.OPEN]: 'status-open',
	[PlanningDayStatus.PLANNING]: 'status-in-planning',
	[PlanningDayStatus.LOCKED]: 'status-locked',
};

const STATUS_LABEL_MAP: Record<DayStatusClass, PlanningDayStatus> = {
	'status-past': PlanningDayStatus.PAST,
	'status-published': PlanningDayStatus.PUBLISHED,
	'status-preview': PlanningDayStatus.PREVIEW,
	'status-open': PlanningDayStatus.OPEN,
	'status-in-planning': PlanningDayStatus.PLANNING,
	'status-locked': PlanningDayStatus.LOCKED,
};

const iconMap: Record<DriverStatKey, string> = {
	work_load: 'mdi-briefcase',
	work_time: 'mdi-clock-outline',
	rest_day: 'mdi-moon-waning-crescent',
	vacation: 'mdi-umbrella-beach',
	preferences_fulfilment: 'mdi-emoticon-outline',
};

const labels: { text: string; value: DriverStatKey }[] = [
	{ text: t('stats.work_load'), value: 'work_load' },
	{ text: t('stats.work_time'), value: 'work_time' },
	{ text: t('stats.rest_day'), value: 'rest_day' },
	{ text: t('stats.vacation'), value: 'vacation' },
	{ text: t('stats.preferences_fulfilment'), value: 'preferences_fulfilment' },
];

const options: StatOption[] = labels.map(({ text, value }) => ({
	text,
	value,
	icon: iconMap[value] || 'mdi-help-circle-outline',
}));

const activeFilter = ref('all');
const selectedDriver = ref<Driver | null>(props.drivers[0] || null);
const currentSelectedMonth = ref<string>(DateTime.now().toFormat('yyyy-MM'));
const dialogState = ref(false);
const activeStatsMenuDriver = ref<Driver | null>(null);
const menu = ref(false);
const previewStore = usePreviewStore();
const snackbarState = ref(false);
const snackbarMsg = shallowRef('');
const calendarGridRef = ref<HTMLElement | null>(null);

const scrollTopPosition = shallowRef(0);
const viewportHeightBounds = shallowRef(600);
const scrollLeftPosition = shallowRef(0);
const viewportWidthBounds = shallowRef(1200);
const scrollWidthBounds = shallowRef(0);
const showAllDrivers = ref(true);

const stateLoading = ref(false);
const loadedDriverIdsSet = shallowRef<Set<string>>(new Set());

const selectedValues = ref<DriverStatKey[]>(['work_load', 'work_time', 'rest_day']);
const applyValues = ref(selectedValues.value);
const applyValuesVersion = ref(0);
const isExpanded = ref(false);
const isUnallocatedExpanded = ref(false);
const showUnallocated = ref(false);

const selectedDuty = ref<DutyItemResponse | null>(null);
const selectedDay = ref<any>(null);
const embeddedTargetDateIso = ref<string | null>(null);

const isScrollRestored = ref(false);
const isProgrammaticScrolling = ref(false);
const cellRefs = new Map<string, HTMLElement>();
const dayStatusActions = ref([]);
const showDayStatusActionMenu = ref(false);
const contextMenuPosition = ref<Position>({
	x: 0,
	y: 0,
});

let scrollTimeoutId: number | null = null;
let rAFScrollId: number | null = null;
let storageTimeoutId: number | null = null;
let isScrollTicking = false;
let resizeObserver: ResizeObserver | null = null;

const toggleUnallocatedExpanded = () => {
	isUnallocatedExpanded.value = !isUnallocatedExpanded.value;

	localStorage.setItem(STORAGE_KEYS.VERTICAL_UNALLOCATED_EXPANDED, JSON.stringify(isUnallocatedExpanded.value));
};

const selectedDayIso = computed(() => selectedDay.value?.dateString || selectedDay.value?.isoDate || '');
const selectedDayId = computed(() => selectedDay.value?.id || null);

const daysArray = computed(() => Object.values(dayStore.days));
const getActiveIndicators = computed(() => planningStore.filters.selectedIndicators);
const dutiesLookup = computed(() => dutyStore.dutiesMappedByDay || {});
const unallocatedDutiesByDay = computed(() => {
	const mapped = dutyStore.unallocatedDutiesMappedByDay || {};

	return Object.fromEntries(
		Object.entries(mapped)
			.map(([dayId, itemsObj]) => {
				const filteredItems = Object.fromEntries(
					Object.entries(itemsObj || {}).filter(([_, item]) => item?.type === 'duty' || item?.type === 'reserve')
				);
				return [dayId, filteredItems];
			})

			.filter(([_, itemsObj]) => Object.keys(itemsObj).length > 0)
	);
});

const isCustomMode = computed(() => !!props.customDuties);

const SelectedStats = computed(() => options.filter((o) => applyValues.value.includes(o.value)));
const { dynamicStatsStyle } = useDriverStatsStyles(() => SelectedStats.value, { columnWidth: 80, extraColumns: () => (dataStore.editor.superuser ? 1 : 0) });

const isDriverFilterActive = computed(() => {
	const allowedIds = planningStore.filters.filteredDrivers;
	if (!allowedIds) return false;
	return allowedIds.length !== planningStore.allDriverIds.length;
});

const buildSearchMatcher = (): ((user: ApiDriverUser) => boolean) | null => {
	const searchQuery = filterCriteria.value.searchQuery;
	if (!searchQuery) return null;

	const hasTrailingComma = searchQuery.trimEnd().endsWith(',');
	const queries = searchQuery
		.split(',')
		.map((value) => value.trim().toLowerCase())
		.filter(Boolean);

	if (!queries.length || hasTrailingComma) return null;

	const selectedDayDutyIndex = selectedDayDutySearchIndex.value;

	return (user: ApiDriverUser) => {
		const driverId = user.id || (user as any)._id;
		const driverName = (user as any).displayName?.toLowerCase() ?? '';
		const dutyName = (selectedDayDutyIndex?.get(driverId) ?? '').toLowerCase();

		return queries.some((query) => driverName.includes(query) || dutyName.includes(query) || doesDriverDutyOrLabelMatchQuery(driverId, query));
	};
};

const filterOptions = computed(() => {
	const allowedIds = planningStore.filters.filteredDrivers;
	const filteredSet = isDriverFilterActive.value ? new Set(allowedIds ?? []) : null;
	const matchesSearch = buildSearchMatcher();

	const isFiltered = (user: ApiDriverUser) => {
		const driverId = user.id || (user as any)._id;
		if (filteredSet && !filteredSet.has(driverId)) return false;
		if (matchesSearch && !matchesSearch(user)) return false;
		return true;
	};

	const activeCount = planningStore.activeUsers.filter(isFiltered).length;
	const inactiveCount = (planningStore.inactiveUsers || []).filter(isFiltered).length;

	return [
		{ label: t('filters.all'), value: 'all', count: activeCount + inactiveCount },
		{ label: t('filters.active'), value: 'active', count: activeCount },
		{ label: t('filters.inactive'), value: 'inactive', count: inactiveCount },
	];
});

const dateRangeBoundaries = computed(() => ({
	fromDate: planningStore.filters.fromDate,
	toDate: planningStore.filters.toDate,
}));

const filterCriteria = computed(() => ({
	filteredDrivers: planningStore.filters.filteredDrivers,
	searchQuery: planningStore.filters.searchQuery?.toLowerCase() || '',
}));

const selectedDayDutySearchIndex = computed(() => {
	const dayId = selectedDayId.value;

	if (!dayId) {
		return null;
	}

	const index = new Map<string, string>();

	for (const [driverId, duties] of Object.entries(dutiesLookup.value)) {
		const duty = (duties as Record<string, any>)[dayId];

		if (duty?.name) {
			index.set(driverId, String(duty.name).toLowerCase());
		}
	}

	return index;
});

const driverIds = computed(() => props.drivers.map((driver) => driver.id));
const applyAffectedFilter = (candidates: Driver[]) => {
	if (!isActive.value || showAllDrivers.value) return candidates;

	return filterByIds(candidates, previewStore.currentPreview?.affectedDrivers, (d) => d.id || d._id);
};

const filteredDrivers = computed(() => {
	if (isCustomMode.value) {
		return props.drivers;
	}

	const { filteredDrivers: allowedList, searchQuery } = filterCriteria.value;
	const allowedIds = allowedList == null ? new Set(driverIds.value) : new Set(allowedList);
	const baseDrivers = props.drivers.filter((driver) => allowedIds.has(driver.id));

	if (!searchQuery) {
		return applyAffectedFilter(baseDrivers);
	}

	const hasTrailingComma = searchQuery.trimEnd().endsWith(',');
	const queries = searchQuery
		.split(',')
		.map((value) => value.trim().toLowerCase())
		.filter(Boolean);

	if (!queries.length) {
		return applyAffectedFilter(baseDrivers);
	}

	const selectedDayDutyIndex = selectedDayDutySearchIndex.value;
	const matchedDrivers: typeof baseDrivers = [];
	const seenIds = new Set<string | number>();

	for (const query of queries) {
		for (const driver of baseDrivers) {
			if (seenIds.has(driver.id)) continue;

			const driverName = driver.displayName?.toLowerCase() ?? '';
			const dutyName = (selectedDayDutyIndex?.get(driver.id) ?? '').toLowerCase();

			if (driverName.includes(query) || dutyName.includes(query) || doesDriverDutyOrLabelMatchQuery(driver.id, query)) {
				matchedDrivers.push(driver);
				seenIds.add(driver.id);
			}
		}
	}

	// No trailing comma → only matches (can be empty → empty state)
	if (!hasTrailingComma) {
		return applyAffectedFilter(matchedDrivers);
	}

	// Trailing comma → matches first, then the rest
	const remainingDrivers = baseDrivers.filter((driver) => !seenIds.has(driver.id));
	return applyAffectedFilter([...matchedDrivers, ...remainingDrivers]);
});
function filterByIds<T>(items: T[], ids: string[] | undefined | null, getId: (x: T) => string): T[] {
	const idSet = new Set(ids ?? []);
	return items.filter((item) => idSet.has(getId(item)));
}

const displayedFilterOptions = ref(filterOptions.value);
watch(
	[filterOptions, () => planningStore.initial],
	([options, initial]) => {
		if (!initial) {
			displayedFilterOptions.value = options;
		}
	},
	{ flush: 'post' }
);

const verticalMetrics = computed(() => {
	const totalCount = filteredDrivers.value.length;
	if (totalCount === 0) return { start: 0, end: 0, padTop: 0, padBottom: 0 };

	if (props.embedded || totalCount <= VERTICAL_BUFFER) {
		return {
			start: 0,
			end: totalCount,
			padTop: 0,
			padBottom: 0,
		};
	}

	const approximateStartIdx = Math.floor(scrollTopPosition.value / ROW_HEIGHT);
	const visibleRowsCount = Math.ceil(viewportHeightBounds.value / ROW_HEIGHT);

	const safeStart = Math.max(0, approximateStartIdx - VERTICAL_BUFFER);
	const safeEnd = Math.min(totalCount, approximateStartIdx + visibleRowsCount + VERTICAL_BUFFER);

	return {
		start: safeStart,
		end: safeEnd,
		padTop: safeStart * ROW_HEIGHT,
		padBottom: (totalCount - safeEnd) * ROW_HEIGHT,
	};
});

const virtualDrivers = computed(() => {
	const { start, end } = verticalMetrics.value;
	return filteredDrivers.value.slice(start, end);
});

const verticalContainerStyle = computed(() => ({
	paddingTop: `${verticalMetrics.value.padTop}px`,
	paddingBottom: `${verticalMetrics.value.padBottom}px`,
}));

const displayedDays = computed(() => {
	const { fromDate, toDate } = dateRangeBoundaries.value;
	if (!fromDate || !toDate) return [];

	const start = new Date(fromDate);
	const end = new Date(toDate);
	const dayCount = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;

	let previousMonthStr = '';
	const daysLookup = dayStore.days;
	const selDayIso = selectedDayIso.value;

	return Array.from({ length: dayCount }, (_, index) => {
		const date = new Date(start);
		date.setDate(date.getDate() + index);

		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const dayNum = String(date.getDate()).padStart(2, '0');
		const isoDate = `${year}-${month}-${dayNum}`;
		const monthStr = `${year}-${month}`;

		const dayOfWeek = date.getDay();
		const weekendClass = dayOfWeek === 0 ? 'sunday-cell' : dayOfWeek === 6 ? 'saturday-cell' : '';
		const isBoundary = index > 0 && monthStr !== previousMonthStr;
		previousMonthStr = monthStr;

		const dayData: DayUiItem = daysLookup[isoDate] ?? { isoDate, isHidden: false, data: {} };
		const statusClass = getDayStatusClass(dayData.data.status);

		return {
			data: dayData,
			id: dayData.data.id ?? dayData.data._id,
			isoDate,
			weekendClass,
			isMonthBoundary: isBoundary,
			weekdayLabel: WEEKDAYS[dayOfWeek === 0 ? 6 : dayOfWeek - 1],
			dayNum,
			statusClass,
			dayClass: {
				[weekendClass]: !!weekendClass,
				'month-boundary': isBoundary,
				'selected-day': selDayIso === isoDate || isoDate === embeddedTargetDateIso.value,
			},
		};
	});
});

const horizontalVirtualMetrics = computed(() => {
	const totalDaysCount = displayedDays.value.length;
	if (totalDaysCount === 0) return { start: 0, end: 0, padLeft: 0, padRight: 0 };

	const totalWidthNeeded = totalDaysCount * CELL_WIDTH;

	if (totalWidthNeeded <= viewportWidthBounds.value || totalDaysCount <= CELL_BUFFER * 2) {
		return {
			start: 0,
			end: totalDaysCount,
			padLeft: 0,
			padRight: 0,
		};
	}

	const approximateStartIdx = Math.floor(scrollLeftPosition.value / CELL_WIDTH);
	const visibleCellsCount = Math.ceil(viewportWidthBounds.value / CELL_WIDTH);

	const safeStart = Math.max(0, approximateStartIdx - CELL_BUFFER);
	const safeEnd = Math.min(totalDaysCount, approximateStartIdx + visibleCellsCount + CELL_BUFFER);

	return {
		start: safeStart,
		end: safeEnd,
		padLeft: safeStart * CELL_WIDTH,
		padRight: (totalDaysCount - safeEnd) * CELL_WIDTH,
	};
});

const virtualDays = computed(() => {
	const { start, end } = horizontalVirtualMetrics.value;
	return displayedDays.value.slice(start, end);
});

const virtualPaddingStyle = computed(() => ({
	paddingLeft: `${horizontalVirtualMetrics.value.padLeft}px`,
	paddingRight: `${horizontalVirtualMetrics.value.padRight}px`,
}));

const totalUnallocatedCount = computed(() => {
	const unallocated = unallocatedDutiesByDay.value;
	return unallocated ? Object.keys(unallocated).length : 0;
});

const availableMonths = computed(() => {
	if (!displayedDays.value.length) return [];
	return [...new Set(displayedDays.value.map((day) => day.isoDate.substring(0, 7)))];
});

const activeVisibleMonth = computed(() => {
	if (!displayedDays.value.length) return currentSelectedMonth.value;

	const viewportCenterOffset = scrollLeftPosition.value + viewportWidthBounds.value / 2;
	const centerDayIndex = Math.floor(viewportCenterOffset / CELL_WIDTH);

	const boundedIndex = Math.max(0, Math.min(centerDayIndex, displayedDays.value.length - 1));
	const activeDay = displayedDays.value[boundedIndex];

	return activeDay ? activeDay.isoDate.substring(0, 7) : currentSelectedMonth.value;
});

const currentMonthPosition = computed(() => {
	const months = availableMonths.value;
	if (!months.length) return -1;

	let idx = months.indexOf(currentSelectedMonth.value);
	if (idx === -1) {
		idx = months.indexOf(activeVisibleMonth.value);
	}
	return idx;
});

const allDaysFitInViewport = computed(() => {
	const totalDaysCount = displayedDays.value.length;
	if (totalDaysCount === 0) return true;

	const totalWidthNeeded = totalDaysCount * CELL_WIDTH;
	return totalWidthNeeded <= viewportWidthBounds.value;
});

const canGoPreviousMonth = computed(() => {
	const months = availableMonths.value;
	if (!months.length || allDaysFitInViewport.value) return false;
	return currentSelectedMonth.value !== months[0];
});

const canGoNextMonth = computed(() => {
	const months = availableMonths.value;
	if (!months.length || allDaysFitInViewport.value) return false;
	return currentSelectedMonth.value !== months[months.length - 1];
});

const filteredMonthHeaders = computed(() => {
	const headers: { key: string; label: string; colspan: number; style: { width: string } }[] = [];
	const days = virtualDays.value;

	for (let i = 0; i < days.length; i++) {
		const monthKey = days[i].isoDate.substring(0, 7);
		const existing = headers.find((h) => h.key === monthKey);
		if (existing) {
			existing.colspan++;
			existing.style.width = `calc(var(--cell-width) * ${existing.colspan})`;
		} else {
			const dt = DateTime.fromISO(days[i].isoDate);
			const monthName = dt.toFormat('LLLL').toLowerCase();
			headers.push({
				key: monthKey,
				label: `${t(`months_short.${monthName}`)} ${dt.toFormat('yy')}`,
				colspan: 1,
				style: { width: 'var(--cell-width)' },
			});
		}
	}
	return headers;
});

const highlightedDriversSet = computed(() => {
	const result = new Set<string>();
	const driversList = props.drivers;
	if (!driversList) return result;

	for (let i = 0; i < driversList.length; i++) {
		if (driversList[i].rotationTimelines?.length > 0) {
			result.add(driversList[i].id);
		}
	}
	return result;
});

const highlightedIntervals = computed(() => {
	const map = new Map<string, [number, number][]>();
	const driversList = props.drivers;
	if (!driversList) {
		return map;
	}
	for (const person of driversList) {
		if (!person.rotationTimelines?.length) {
			continue;
		}
		const intervals: [number, number][] = person.rotationTimelines.map((timeline) => [
			DateTime.fromISO(timeline.from).toMillis(),
			DateTime.fromISO(timeline.to).toMillis(),
		]);
		map.set(person.id, intervals);
	}
	return map;
});

const isCellHighlighted = (personId: string, isoDate: string): boolean => {
	const intervals = highlightedIntervals.value.get(personId);
	if (!intervals) {
		return false;
	}
	const ms = DateTime.fromISO(isoDate).toMillis();
	return intervals.some(([start, end]) => ms >= start && ms <= end);
};

const isTodayInRange = computed(() => {
	const { fromDate, toDate } = dateRangeBoundaries.value;
	if (!fromDate || !toDate) return false;

	const today = DateTime.now().startOf('day');
	const start = DateTime.fromISO(fromDate).startOf('day');
	const end = DateTime.fromISO(toDate).startOf('day');

	return today >= start && today <= end;
});

const isDutyInPreview = (personId: string, day: DayItem) => {
	const dayId = day?.id;
	if (!dayId || !personId || previewStore.changesPanelActiveTab !== 'changes') return false;

	const logsForDay = previewStore.changesMappedByDay?.[dayId] ?? [];
	if (!logsForDay.length) return false;

	const duty = dutiesLookup.value?.[personId]?.[dayId];
	const hasOriginal = duty?.original !== undefined;
	const dutyKey = duty?.original || duty?.id || duty?.uuid;

	return logsForDay.some((log) => (hasOriginal && (log.affectedDuties ?? []).includes(dutyKey)) || (log.affectedDrivers ?? []).includes(personId));
};

const isSelectedChangeLogDuty = (personId: string, day: DayItem) => {
	const dayId = day?.id;
	if (!dayId || !personId || previewStore.changesPanelActiveTab !== 'changes') return false;

	const log = previewStore.selectedChangeLog;
	if (log?.day !== dayId) return false;

	const duty = dutiesLookup.value?.[personId]?.[dayId];
	const dutyKey = duty?.original || duty?.id || duty?.uuid;

	return (dutyKey !== undefined && (log.affectedDuties ?? []).includes(dutyKey)) || (log.affectedDrivers ?? []).includes(personId);
};

const isDutyInViolation = (personId: string, day: DayItem) => {
	if (!day?.id || !personId || previewStore.changesPanelActiveTab !== 'violations') return false;

	const logsForDay = previewStore.previewViolationsMappedByDay?.[day.id] ?? [];
	if (!logsForDay.length) return false;

	const original = dutiesLookup.value?.[personId]?.[day.id]?.original;

	return logsForDay.some((log) => (original !== undefined && log.duty !== undefined && log.duty === original) || log.user === personId);
};

const isSelectedViolationDuty = (personId: string, day: DayItem) => {
	if (!day?.id || !personId || previewStore.changesPanelActiveTab !== 'violations') return false;

	const original = dutiesLookup.value?.[personId]?.[day?.id]?.original;

	return (
		previewStore.selectedChangeLog?.day === day.id &&
		((previewStore.selectedChangeLog.duty !== undefined && original !== undefined && previewStore.selectedChangeLog.duty === original) ||
			previewStore.selectedChangeLog.user === personId) &&
		VIOLATION_CATEGORIES.includes(previewStore.selectedChangeLog?.category) &&
		previewStore.changesPanelActiveTab === 'violations'
	);
};

const dutyByCellKey = computed(() => {
	const map = new Map<string, DutyItemResponse>();
	const source = isCustomMode.value ? props.customDuties : dutiesLookup.value;
	for (const [personId, dutiesForPerson] of Object.entries(source || {})) {
		for (const [key, duty] of Object.entries(dutiesForPerson || {})) {
			map.set(`${personId}_${key}`, duty as DutyItemResponse);
		}
	}
	return map;
});

const getDutyForCell = (personId: string, day: DayUiItem) => dutyByCellKey.value.get(`${personId}_${isCustomMode.value ? day.isoDate : day.id}`) ?? null;

const getCellPreviewStyle = (personId: string, day: DayItem): CSSProperties => {
	const d = getDutyForCell(personId, day) as any;
	return getDutyCellStyle(d);
};

const setCellRef = (el: any, driverId: string, dayIso: string) => {
	const key = `cell-${driverId}-${dayIso}`;
	if (el) {
		cellRefs.set(key, el as HTMLElement);
	} else {
		cellRefs.delete(key);
	}
};

const markChange = (driverId: string, dayIso: string, duration: number = 4000) => {
	const key = `cell-${driverId}-${dayIso}`;
	const cellEl = cellRefs.get(key);

	if (cellEl) {
		cellEl.classList.add('changed');

		setTimeout(() => {
			if (cellRefs.has(key)) {
				cellEl.classList.remove('changed');
			}
		}, duration);
	}
};

const handleUpdateDuty = (updatedDuty: DutyItemResponse) => {
	selectedDuty.value = {
		...selectedDuty.value,
		...updatedDuty,
	};

	if (updatedDuty && selectedDriver.value && selectedDay.value) {
		markChange(selectedDriver.value.id, selectedDay.value.dateString, 4000);
	}
};

const updatePlanningTableStorage = (patch: Record<string, any>) => {
	try {
		const raw = localStorage.getItem(STORAGE_KEYS.PLANNING_TABLE_DATA);
		const currentData = raw ? JSON.parse(raw) : {};
		localStorage.setItem(STORAGE_KEYS.PLANNING_TABLE_DATA, JSON.stringify({ ...currentData, ...patch }));
		previewStore.refreshPlanningDateRange();
	} catch (e) {
		console.error('Failed to write planning table state to localStorage:', e);
	}
};

const debouncedUpdateStorage = (patch: Record<string, any>) => {
	if (storageTimeoutId !== null) clearTimeout(storageTimeoutId);

	storageTimeoutId = window.setTimeout(() => {
		updatePlanningTableStorage(patch);
		storageTimeoutId = null;
	}, 100);
};

const refreshStatistics = async () => {
	stateLoading.value = true;
	try {
		const date = selectedDay.value?.dateString || DateTime.now().toISODate();
		const users = planningStore.filters.filteredDrivers || virtualDrivers.value.map((d) => d.id);
		await planningStore.getStatistics(date, users);
	} catch (error) {
		console.error('Failed to update statistics:', error);
	} finally {
		stateLoading.value = false;
	}
};

const getDayStatusClass = (status?: PlanningDayStatus): DayStatusClass | '' => (status ? (STATUS_CLASS_MAP[status] ?? '') : '');
const getStatusLabel = (statusClass: DayStatusClass | ''): string => (statusClass ? t(`planning_duty_status_${STATUS_LABEL_MAP[statusClass]}`) : '');

const normalizedTargetDate = computed(() => {
	if (!props.targetDate) return null;
	const dt = DateTime.fromISO(props.targetDate);
	return dt.isValid ? dt.toISODate() : null;
});

const isFirstStatusCell = (index: number): boolean => {
	if (index === 0) return true;
	const currentStatus = virtualDays.value[index]?.statusClass;
	const previousStatus = virtualDays.value[index - 1]?.statusClass;
	return currentStatus !== previousStatus;
};

const scrollToCell = async (driverId: string | null, dayId: string | null) => {
	if (!calendarGridRef.value) return;

	const dayObj = displayedDays.value.find((day) => day.id === dayId || day.data?.data?.id === dayId || day.isoDate === dayId);
	if (!dayObj) return;

	selectedDay.value = dayObj.data?.data || dayObj;
	planningStore.setSelectedDay(dayObj);

	if (driverId) {
		const driverObj = filteredDrivers.value.find((d) => d.id === driverId);
		if (driverObj) {
			selectDriver(driverObj);
		}
	}

	await nextTick();

	const dateIndex = displayedDays.value.findIndex((day) => day.isoDate === dayObj.isoDate);
	let targetLeft = scrollLeftPosition.value;
	if (dateIndex !== -1) {
		const viewportWidth = calendarGridRef.value.clientWidth;
		targetLeft = Math.max(0, dateIndex * CELL_WIDTH - viewportWidth / 2 + CELL_WIDTH / 2);
	}

	let targetTop = scrollTopPosition.value;
	if (driverId) {
		const driverIndex = filteredDrivers.value.findIndex((d) => d.id === driverId);
		if (driverIndex !== -1) {
			const viewportHeight = calendarGridRef.value.clientHeight;
			const HEADER_OFFSET = 94;
			targetTop = Math.max(0, driverIndex * ROW_HEIGHT - viewportHeight / 2 + HEADER_OFFSET);
		}
	}

	isProgrammaticScrolling.value = true;
	calendarGridRef.value.scrollTo({
		left: targetLeft,
		top: targetTop,
		behavior: 'smooth',
	});

	setTimeout(() => {
		isProgrammaticScrolling.value = false;
		if (calendarGridRef.value) {
			scrollLeftPosition.value = calendarGridRef.value.scrollLeft;
			scrollTopPosition.value = calendarGridRef.value.scrollTop;
		}
	}, 450);
};

const restoreScrollPosition = async () => {
	try {
		const savedScroll = localStorage.getItem(SCROLL_STORAGE_KEY);
		if (!savedScroll || !calendarGridRef.value) return;

		const { left, top } = JSON.parse(savedScroll);

		await nextTick();

		if (calendarGridRef.value) {
			isProgrammaticScrolling.value = true;
			calendarGridRef.value.scrollLeft = left || 0;
			calendarGridRef.value.scrollTop = top || 0;

			scrollLeftPosition.value = left || 0;
			scrollTopPosition.value = top || 0;
			viewportWidthBounds.value = calendarGridRef.value.clientWidth;
			viewportHeightBounds.value = calendarGridRef.value.clientHeight;
			scrollWidthBounds.value = calendarGridRef.value.scrollWidth;

			currentSelectedMonth.value = activeVisibleMonth.value;

			setTimeout(() => {
				isProgrammaticScrolling.value = false;
			}, 50);
		}
	} catch (e) {
		console.error('Failed to restore scroll position:', e);
	} finally {
		isScrollRestored.value = true;
	}
};

const handleMasterScroll = (e: Event) => {
	const target = e.target as HTMLElement;

	if (!isScrollTicking) {
		rAFScrollId = requestAnimationFrame(() => {
			if (target) {
				if (Math.abs(scrollLeftPosition.value - target.scrollLeft) > 1) {
					scrollLeftPosition.value = target.scrollLeft;
				}
				if (Math.abs(scrollTopPosition.value - target.scrollTop) > 1) {
					scrollTopPosition.value = target.scrollTop;
				}
				viewportWidthBounds.value = target.clientWidth;
				viewportHeightBounds.value = target.clientHeight;
				scrollWidthBounds.value = target.scrollWidth;
			}
			isScrollTicking = false;
		});
		isScrollTicking = true;
	}

	if (scrollTimeoutId !== null) clearTimeout(scrollTimeoutId);

	scrollTimeoutId = window.setTimeout(() => {
		scrollTimeoutId = null;

		if (target && isScrollRestored.value && !isProgrammaticScrolling.value && !props.embedded) {
			try {
				localStorage.setItem(
					SCROLL_STORAGE_KEY,
					JSON.stringify({
						left: target.scrollLeft,
						top: target.scrollTop,
					})
				);
			} catch (err) {
				console.error('Failed to save scroll position:', err);
			}
		}
	}, 200);
};

const updateViewportDimensions = () => {
	if (calendarGridRef.value) {
		viewportWidthBounds.value = calendarGridRef.value.clientWidth || 1200;
		viewportHeightBounds.value = calendarGridRef.value.clientHeight || 600;
		scrollWidthBounds.value = calendarGridRef.value.scrollWidth || 0;
	}
};

const setSelectedDuty = async (duty: DutyItemResponse, person: ApiDriverUser | null) => {
	if (person) {
		planningStore.isStreamingPaused = true;
		if (!userStore.isDriverActiveOnDate(person, selectedDay.value?.dateString)) {
			snackbarState.value = true;
			snackbarMsg.value = t('snackbar_info_text_user_inactive');
			return;
		}
	}

	selectedDuty.value = duty;
	planningStore.setSelectedDuty(duty);
	planningStore.setSelectedDriver(person as any);
	dialogState.value = true;
};

const selectDriver = (driver: Driver) => {
	planningStore.setSelectedDriver(driver);
	selectedDriver.value = driver;

	updatePlanningTableStorage({ selectedDriver: driver });
};

const setSelectedDay = async (day: DayItem | null) => {
	const previousDateString = selectedDay.value?.dateString;
	const nextDateString = day?.data?.data?.dateString;

	selectedDay.value = day.data.data;
	planningStore.setSelectedDay(day);

	updatePlanningTableStorage({ selectedDay: day.data.data });

	if (nextDateString && nextDateString === previousDateString) return;

	await refreshStatistics();
};

const toggleSidebar = () => {
	isExpanded.value = !isExpanded.value;
	localStorage.setItem(STORAGE_KEYS.EXPANDED, JSON.stringify(isExpanded.value));
};

const toggleShowUnallocated = async () => {
	showUnallocated.value = !showUnallocated.value;
	if (showUnallocated.value && Object.keys(dutyStore.unallocatedDutiesMappedByDay).length === 0) {
		await dutyStore.getUnallocatedDuties({ days: Object.keys(dayStore.daysRef) });
	}
	localStorage.setItem(STORAGE_KEYS.SHOW_UNALLOCATED, JSON.stringify(showUnallocated.value));
};

const apply = (updatedValues: DriverStatKey[]) => {
	selectedValues.value = [...updatedValues];
	applyValues.value = [...updatedValues];
	applyValuesVersion.value++;
	planningStore.filters.selectedStats = selectedValues.value;
	debouncedUpdateStorage({ selectedStats: selectedValues.value });
	menu.value = false;
};

const resetMenu = () => {
	const defaults: DriverStatKey[] = ['work_load', 'work_time', 'rest_day'];
	selectedValues.value = [...defaults];
	applyValues.value = [...defaults];
	applyValuesVersion.value++;
	planningStore.filters.selectedStats = selectedValues.value;
	debouncedUpdateStorage({ selectedStats: selectedValues.value });
	menu.value = false;
};

const scrollToMonth = async (monthKey: string) => {
	await nextTick();
	if (!calendarGridRef.value) return;

	const monthIndex = displayedDays.value.findIndex((day) => day.isoDate.startsWith(monthKey));
	if (monthIndex === -1) return;

	const targetLeft = monthIndex * CELL_WIDTH;

	isProgrammaticScrolling.value = true;
	calendarGridRef.value.scrollTo({
		left: targetLeft,
		behavior: 'smooth',
	});

	setTimeout(() => {
		isProgrammaticScrolling.value = false;
		if (calendarGridRef.value) {
			scrollLeftPosition.value = calendarGridRef.value.scrollLeft;
		}
	}, 450);
};

const navigateMonth = async (direction: number) => {
	const months = availableMonths.value;
	if (!months.length || !calendarGridRef.value) return;

	const visibleIdx = months.indexOf(activeVisibleMonth.value);
	let targetIdx = visibleIdx !== -1 ? visibleIdx : currentMonthPosition.value;

	if (targetIdx === -1) {
		targetIdx = direction > 0 ? 0 : months.length - 1;
	} else {
		targetIdx += direction;
	}

	if (targetIdx < 0) targetIdx = 0;
	if (targetIdx >= months.length) targetIdx = months.length - 1;

	currentSelectedMonth.value = months[targetIdx];

	await scrollToMonth(months[targetIdx]);
};

const scrollToDate = async (isoDate: string, centered = false) => {
	await nextTick();
	if (!calendarGridRef.value) {
		return;
	}

	const dateIndex = displayedDays.value.findIndex((day) => day.isoDate === isoDate);
	if (dateIndex === -1) {
		return;
	}

	let targetScrollLeft = dateIndex * CELL_WIDTH;
	if (centered) {
		const viewportWidth = calendarGridRef.value.clientWidth;
		targetScrollLeft = Math.max(0, targetScrollLeft - viewportWidth / 2 + CELL_WIDTH / 2);
	}

	isProgrammaticScrolling.value = true;
	calendarGridRef.value.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });

	setTimeout(() => {
		isProgrammaticScrolling.value = false;
	}, 400);
};

const goToCurrentMonth = async () => {
	const todayIsoDate = DateTime.now().toISODate();
	const todayMonthKey = DateTime.now().toFormat('yyyy-MM');

	const todayDayObj = displayedDays.value.find((day) => day.isoDate === todayIsoDate);

	if (todayDayObj) {
		selectedDay.value = todayDayObj.data.data;
		planningStore.setSelectedDay(todayDayObj);
		updatePlanningTableStorage({ selectedDay: todayDayObj.data.data });

		currentSelectedMonth.value = todayMonthKey;

		await scrollToDate(todayIsoDate);
		await refreshStatistics();
	} else {
		await scrollToMonth(todayMonthKey);
	}
};

const closeDialog = () => {
	dialogState.value = false;
	planningStore.isStreamingPaused = false;
};

const highlightOnSingleClick = (person, day) => {
	selectDriver(person);
	setSelectedDay(day);
};

const handleGridDelegatedDblClick = (event: MouseEvent) => {
	const target = event.target as HTMLElement;
	const cellElement = target.closest('.grid-cell') as HTMLElement;
	if (!cellElement) return;

	const driverId = cellElement.dataset.driverId;
	const dayId = cellElement.dataset.dayId;
	const isoDate = cellElement.dataset.isoDate;

	if (!checkIfDayExists(isoDate!)) {
		snackbarState.value = true;
		snackbarMsg.value = t('duty_create_error_without_day', { day: isoDate });
		return;
	}

	if (!driverId || !isoDate || !dayId) return;

	const person = props.drivers.find((d) => d.id === driverId);

	if (!person) return;

	const dutyTarget = target.closest('[data-type]') as HTMLElement;
	const dutyId = dutyTarget?.dataset.dutyId;

	const actualDuty = isCustomMode.value ? (props.customDuties?.[driverId]?.[isoDate] ?? null) : dutiesLookup.value[driverId]?.[dayId];

	if (dutyId && actualDuty) {
		setSelectedDuty(actualDuty, person as any);
		return;
	}

	if (actualDuty) {
		return;
	}

	if (isCustomMode.value) {
		return;
	}

	if (!userStore.isDriverActiveOnDate(person, selectedDay.value?.dateString)) {
		snackbarState.value = true;
		snackbarMsg.value = t('snackbar_info_text_user_inactive');
		return;
	}

	if (dayStore.getDayStatus(isoDate) === 'locked') {
		snackbarState.value = true;
		snackbarMsg.value = t('duty_create_error_locked_day');
		return;
	}

	if (!planningStore.canEditDutyForDate(isoDate)) {
		snackbarState.value = true;
		snackbarMsg.value = t('snackbar_info_text_locked_days');
		return;
	}

	assignDutyStore.openModal({
		date: isoDate,
		dayId: null,
		driverId: person.id,
		driverName: `${person.lastName}, ${person.firstName}`,
		isLocked: true,
	});
};

const handleCellContextMenu = (event: MouseEvent, person: Driver, day: DayItem) => {
	if (!userStore.isDriverActiveOnDate(person, day.isoDate)) return;

	absenceMenuStore.openContextMenu({
		x: event.clientX,
		y: event.clientY,
		driverId: person.id,
		driverName: `${person.lastName}, ${person.firstName}`,
		date: day.isoDate,
	});
};

const loadDutiesForVisibleDrivers = async () => {
	if (isCustomMode.value) {
		return;
	}
	const { fromDate, toDate } = dateRangeBoundaries.value;
	if (!fromDate || !toDate) return;

	const from = DateTime.fromISO(fromDate);
	const to = DateTime.fromISO(toDate);
	const targetUsers =
		planningStore.filters.filteredDrivers && planningStore.filters.filteredDrivers.length > 0
			? planningStore.filters.filteredDrivers
			: props.drivers.map((d: any) => d.id || d._id).filter(Boolean);

	dutyStore.startBackgroundDutiesFetch({
		from,
		to,
		statsDate: selectedDay.value?.dateString,
		users: targetUsers,
		chunkSize: 20,
	});
};

const setUserActiveFilter = (value: string) => {
	planningStore.filters.userStatus = value;
	updatePlanningTableStorage({ userStatus: value });
};

const checkIfDayExists = (isoDate: string) => {
	const daysLookup = dayStore.days;
	const dayData = daysLookup[isoDate];
	return !!dayData;
};

function doesDriverDutyOrLabelMatchQuery(driverId: string, query: string): boolean {
	if (!query) return false;

	const dayId = selectedDayId.value;
	if (!dayId) return false;

	const driverDuties = dutiesLookup.value[driverId];
	if (!driverDuties) return false;

	const duty = driverDuties[dayId];
	if (!duty) return false;

	const lowerQuery = query.toLowerCase();
	const isDutyNameMatch = duty.name?.toLowerCase().includes(query.toLowerCase());
	const dutyLabelMatch = duty.labels?.some((label: any) => label?.name?.toLowerCase().includes(lowerQuery));
	return isDutyNameMatch || dutyLabelMatch;
}

const isContextMenuVisible = (day) => {
	const status = dayStore.getDayStatus(day.isoDate);
	return status === PLANNING_DAY_STATUS.PLANNING || (status === PLANNING_DAY_STATUS.PREVIEW && usePlanningBridgeStore().hasPermission);
};

const onClickContextMenu = async (item: DayStatusAction) => {
	const day = item.day.data;
	const currentStatus = dayStore.getDayStatus(day.isoDate);
	const newStatus = currentStatus === PLANNING_DAY_STATUS.PLANNING ? PLANNING_DAY_STATUS.PREVIEW : PLANNING_DAY_STATUS.PLANNING;
	const singleEntrypayload: dayStatusPayload = {
		id: day.data._id,
		status: newStatus,
	};
	const multipleEntryPayload = {
		status: newStatus,
	};
	if (item.mode === 'single') {
		await dayStore.updateDayStatus(singleEntrypayload);
	} else {
		await dayStore.updateDayStatus(multipleEntryPayload);
	}
};

const showContextMenu = (e, day) => {
	const dayStatus = dayStore.getDayStatus(day.isoDate);
	if (dayStatus !== PLANNING_DAY_STATUS.PLANNING && dayStatus !== PLANNING_DAY_STATUS.PREVIEW) {
		return;
	}

	if (dayStatus === PLANNING_DAY_STATUS.PLANNING) {
		dayStatusActions.value = [
			{ title: t('planning_switch_to_preview'), day: day, mode: 'single' },
			{ title: t('planning_switch_all_to_preview'), day: day, mode: 'all' },
		];
	} else {
		dayStatusActions.value = [
			{ title: t('planning_switch_to_planning'), day: day, mode: 'single' },
			{ title: t('planning_switch_all_to_planning'), day: day, mode: 'all' },
		];
	}

	contextMenuPosition.value.x = e.clientX;
	contextMenuPosition.value.y = e.clientY;
	e.preventDefault();

	showDayStatusActionMenu.value = true;

	nextTick(() => {
		showDayStatusActionMenu.value = true;
	});
};

watch(
	() => activeVisibleMonth.value,
	(newMonth) => {
		if (newMonth && currentSelectedMonth.value !== newMonth && !isProgrammaticScrolling.value) {
			currentSelectedMonth.value = newMonth;
		}
	}
);

const waitForCalendarGridRef = async (maxAttempts = 20): Promise<boolean> => {
	for (let i = 0; i < maxAttempts; i++) {
		if (calendarGridRef.value) return true;
		await nextTick();
	}
	return !!calendarGridRef.value;
};

const adjustScrollPosition = () => {
	const driverId = previewStore.selectedChangeLog.user ?? previewStore.selectedChangeLog.userId ?? null;
	const dayId = previewStore.selectedChangeLog.day ?? previewStore.selectedChangeLog.dayId ?? previewStore.selectedChangeLog.date ?? null;

	if (driverId || dayId) {
		scrollToCell(driverId, dayId);
	}
};

watch(
	() => [planningStore.filters.fromDate, planningStore.filters.toDate],
	([newFrom, newTo], [oldFrom, oldTo]) => {
		if (newFrom !== oldFrom || newTo !== oldTo) {
			loadedDriverIdsSet.value = new Set();
			scrollLeftPosition.value = 0;
			scrollTopPosition.value = 0;
			if (calendarGridRef.value) {
				isProgrammaticScrolling.value = true;
				calendarGridRef.value.scrollLeft = 0;
				calendarGridRef.value.scrollTop = 0;
				setTimeout(() => {
					isProgrammaticScrolling.value = false;
				}, 400);
			}
			nextTick(() => loadDutiesForVisibleDrivers());
		}
	}
);

watch(
	() => planningStore?.initial,
	async (isInitialLoading) => {
		if (isInitialLoading) return;

		if (props.embedded && normalizedTargetDate.value) {
			const targetIso = normalizedTargetDate.value;
			const targetDayObj = displayedDays.value.find((day) => day.isoDate === targetIso);

			selectedDay.value = { dateString: targetIso, isoDate: targetIso, ...targetDayObj?.data.data };
			embeddedTargetDateIso.value = targetIso;
			if (targetDayObj) planningStore.setSelectedDay(targetDayObj);

			currentSelectedMonth.value = normalizedTargetDate.value.substring(0, 7);
			await scrollToDate(normalizedTargetDate.value, true);
			isScrollRestored.value = true;
		} else {
			const currentMonthString = DateTime.now().toFormat('yyyy-MM');
			const exists = daysArray.value.some((item) => item.data?.id === selectedDay.value?.id);

			if (!exists) {
				selectedDay.value = daysArray.value.at(0)?.data ?? null;
			}
			if (!selectedDriver.value) selectedDriver.value = virtualDrivers.value[0];

			updatePlanningTableStorage({ selectedDay: selectedDay.value, selectedDriver: selectedDriver.value });

			currentSelectedMonth.value = daysArray.value.some((day) => day.isoDate.substring(0, 7) === currentMonthString)
				? currentMonthString
				: daysArray.value[0]?.isoDate.substring(0, 7) || currentMonthString;

			const hasSavedScroll = localStorage.getItem(SCROLL_STORAGE_KEY);
			if (hasSavedScroll) {
				await restoreScrollPosition();
			} else {
				await waitForCalendarGridRef();

				const todayIsoDate = DateTime.now().toISODate();
				const isTodayInRange = displayedDays.value.some((day) => day.isoDate === todayIsoDate);

				if (isTodayInRange) {
					await scrollToDate(todayIsoDate, true);
				} else if (calendarGridRef.value) {
					isProgrammaticScrolling.value = true;
					calendarGridRef.value.scrollLeft = 0;
					scrollLeftPosition.value = 0;
					setTimeout(() => {
						isProgrammaticScrolling.value = false;
					}, 400);
				}
				isScrollRestored.value = true;
			}
		}

		nextTick(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					metricsService.endTracking('PlanningDutyTable-Init', 'PlanningDutyTable.vue');
				});
			});
		});
	},
	{ immediate: true }
);

watch(
	() => dutyStore?.updateStatistics,
	async (val) => {
		if (val) {
			await refreshStatistics();
		}
	}
);

const areIdArraysEqual = (arr1: string[], arr2: string[]): boolean => {
	if (arr1 === arr2) return true;
	if (arr1.length !== arr2.length) return false;
	for (let i = 0; i < arr1.length; i++) {
		if (arr1[i] !== arr2[i]) return false;
	}
	return true;
};

watch(
	() => filteredDrivers.value,
	(newDrivers) => {
		const newIds = newDrivers.map((driver) => driver.id);
		const currentIds = planningStore.filters.combinedFilteredDrivers || [];
		if (!areIdArraysEqual(newIds, currentIds)) {
			planningStore.filters.combinedFilteredDrivers = newIds;
			debouncedUpdateStorage({ combinedFilteredDrivers: newIds });
		}
	},
	{ immediate: true }
);

watch(
	() => previewStore.selectedChangeLog,
	(selectedLog) => {
		if (selectedLog) {
			adjustScrollPosition();
		}
	},
	{ deep: true, immediate: true }
);

watch(
	() => previewStore.cardClicked,
	() => {
		adjustScrollPosition();
	}
);

watch(
	() => previewStore.currentPreview,
	(preview) => {
		if (!preview) {
			showAllDrivers.value = true;
		}
	},
	{ deep: true, immediate: true }
);

onBeforeMount(async () => {
	metricsService.startTracking('PlanningDutyTable-Init');

	if (props.embedded) {
		isExpanded.value = false;
	} else {
		const savedExpanded = localStorage.getItem(STORAGE_KEYS.EXPANDED);
		if (savedExpanded) isExpanded.value = JSON.parse(savedExpanded);
	}

	const savedUnallocatedExpanded = localStorage.getItem(STORAGE_KEYS.VERTICAL_UNALLOCATED_EXPANDED);
	if (savedUnallocatedExpanded) {
		isUnallocatedExpanded.value = JSON.parse(savedUnallocatedExpanded);
	}

	const savedUnallocated = localStorage.getItem(STORAGE_KEYS.SHOW_UNALLOCATED);
	const showUnallocatedDuties = savedUnallocated ? JSON.parse(savedUnallocated) : false;
	showUnallocated.value = showUnallocatedDuties;

	const planningTableData = JSON.parse(localStorage.getItem(STORAGE_KEYS.PLANNING_TABLE_DATA) || '{}');

	if (planningTableData.selectedDay) {
		selectedDay.value = planningTableData.selectedDay;
	}

	if (planningTableData.selectedDriver) {
		selectedDriver.value = planningTableData.selectedDriver;
	}

	if (planningTableData.selectedStats) {
		planningStore.filters.selectedStats = planningTableData.selectedStats;
		selectedValues.value = planningTableData.selectedStats;
		applyValues.value = planningTableData.selectedStats;
	} else {
		planningStore.filters.selectedStats = selectedValues.value;
	}

	if (planningStore.filters.userStatus) {
		activeFilter.value = planningStore.filters.userStatus;
	}
});

onMounted(() => {
	updateViewportDimensions();

	if (calendarGridRef.value) {
		resizeObserver = new ResizeObserver(() => {
			updateViewportDimensions();
		});
		resizeObserver.observe(calendarGridRef.value);
	}
});

onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect();
	}
	if (storageTimeoutId !== null) clearTimeout(storageTimeoutId);
	if (scrollTimeoutId !== null) clearTimeout(scrollTimeoutId);
	if (rAFScrollId !== null) cancelAnimationFrame(rAFScrollId);
});
</script>

<style lang="scss" scoped>
.sidebar-column-sticky {
	position: sticky;
	left: 0;
	z-index: 15;
	background-color: #ffffff;
	border-right: 1px solid #e2e8f0;
	display: flex;
	align-items: center;
	width: fit-content;
	flex-shrink: 0;
}

.sidebar-title,
.sidebar-fixed-title-part {
	width: calc(var(--sidebar-width, 290px) + var(--checkbox-width, 0px));
	min-width: calc(var(--sidebar-width, 290px) + var(--checkbox-width, 0px));
	max-width: calc(var(--sidebar-width, 290px) + var(--checkbox-width, 0px));
	flex: 0 0 calc(var(--sidebar-width, 290px) + var(--checkbox-width, 0px));
	box-sizing: border-box;
	font-weight: 700;
	font-size: 14px;
	color: #0f172a;
}

.sidebar-col-stats,
.sidebar-stats-part {
	height: 100%;
	overflow: hidden;
	box-sizing: border-box;
	flex-shrink: 0;
}

.stat-header-cell {
	width: 80px;
	min-width: 80px;
	max-width: 80px;
	flex: 0 0 80px;
	box-sizing: border-box;
}

.calendar-body {
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
}

.scheduler-container {
	--row-height: 40px;
	--unallocated-height: 90px;
	--sidebar-width: 290px;
	--checkbox-width: 0px;
	--cell-width: 70px;
	--header-height: 35px;
	border: 1px solid #e2e8f0;
	height: calc(100vh - 220px);
	overflow: hidden;
	background-color: #ffffff;
	display: flex;
	flex-direction: column;
	position: relative;

	&.is-selectable {
		--checkbox-width: 36px;
	}

	&.is-embedded {
		height: auto !important;
		max-height: 450px;
		border: none !important;
		box-shadow: none !important;
	}
}

.is-embedded .calendar-master-scroll-viewport {
	flex-grow: 0;
	height: auto !important;
	overflow-y: auto;
}

.calendar-master-scroll-viewport {
	flex-grow: 1;
	overflow-x: auto;
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: #10565c #f1f1f1;
	position: relative;

	&::-webkit-scrollbar {
		width: 8px;
		height: 8px;
	}
	&::-webkit-scrollbar-track {
		background: #f1f1f1;
	}
	&::-webkit-scrollbar-thumb {
		background: #10565c;
		border-radius: 4px;
	}
}

.scheduler-row {
	display: flex;
	width: max-content;
}

.calendar-header-sticky {
	position: sticky;
	top: 0;
	z-index: 20;
	background: white;
}

.calendar-column-flow {
	display: flex;
	flex-direction: row;
	height: 100%;
	box-sizing: border-box;
}

.layout-month-header-row {
	height: 26px;
	background-color: #ffffff;

	.month-header-cell {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		padding-left: 12px;
		font-size: 11px;
		color: #475569;
		letter-spacing: 0.05em;
		border-right: 2px solid #cbd5e1;
		background-color: #f1f5f9;
		height: 100%;
	}
}

.layout-status-row {
	height: 16px;
	align-items: center;

	.sidebar-column-sticky {
		height: 100%;
	}
}

.layout-top-nav-row {
	height: 45px;
	background-color: #ffffff;
	display: flex;
	width: 100%;
}

.layout-date-row {
	height: 52px;
	background-color: #f8fafc;
}

.content-row {
	height: var(--row-height);
}

.day-cell {
	width: var(--cell-width);
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border-right: 1px solid #f1f5f9;
	background: #f8fafc;
	flex-shrink: 0;

	.weekday-text {
		font-size: 9px;
		color: #94a3b8;
		font-weight: 600;
	}
	.day-number-text {
		font-size: 14px;
		font-weight: 700;
		color: #1e293b;
	}
}

.grid-cell {
	width: var(--cell-width);
	height: 100%;
	border-right: 1px solid #f1f5f9;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	contain: strict;

	&.changed {
		background-color: rgb(240, 203, 203) !important;
	}
}

.saturday-cell {
	background-color: #f7f7f7;
}

.sunday-cell {
	background-color: #ebf9ff !important;
}

.highlighted-duty {
	background: #eeeeee;
}

.border-bottom {
	border-bottom: 1px solid #e2e8f0;
}

.header-border-bottom {
	border-bottom: 1px solid #e2e8f0;
}

.status-past {
	background: #177a82;
}

.status-published {
	background: #4caf50;
}

.status-preview {
	background: #fdef96;
}

.status-locked {
	background: #b5b5b5;
}

.status-in-planning {
	background: #71dadf;
}

.status-open {
	background: #ebebeb;
}

.status-empty {
	background: transparent;
}

.border-right {
	border-right: 1px solid #eee;
}

.selected-day {
	background-color: #fff2e2 !important;
}

.selected-driver-row {
	background-color: #fff2e2 !important;
}

.violations-trailing-col {
	height: 100%;
	flex-shrink: 0;
	white-space: nowrap;
	padding-right: 12px;
}

.planning-status-cell {
	width: var(--cell-width);
	height: 100%;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	padding-left: 4px;
	position: relative;
	overflow: visible;

	.status-cell-label {
		font-size: 9px;
		font-weight: 700;
		line-height: 1;
		text-transform: uppercase;
		color: #222222;
		white-space: nowrap;
		pointer-events: none;
		letter-spacing: 0.03em;
		text-shadow: 0px 0px 2px rgba(0, 0, 0, 0.4);
	}
}

.preview-dropdown {
	padding: 4px;
	background-color: white;
	border-radius: 4px;
	box-shadow: 0 1px 1px 1px rgba(232, 240, 246, 0.75);
}

.preview-change {
	outline: 3px solid #45a1f1ab;
	outline-offset: -2px;
	border-radius: 4px;
}

.preview-selected-change {
	outline: 3px solid #0960ed;
	outline-offset: -2px;
	border-radius: 4px;
}

.preview-violation {
	outline: 3px solid #e7000b66;
	outline-offset: -3px;
	border-radius: 4px;
}

.preview-selected-violation {
	outline: 3px solid #e7000b;
	outline-offset: -3px;
	border-radius: 4px;
}

.empty-drivers-state {
	height: 300px;
	width: 100%;
}

.switch-label-front :deep(.v-selection-control) {
	flex-direction: row-reverse;
	justify-content: flex-start;
	font-size: 14px;
	transform: scale(0.8);
}
.switch-label-front :deep(.v-label) {
	padding-inline-start: 0;
	padding-inline-end: 12px;
	margin-inline-start: 0;
	font-size: 14px;
}
</style>
